import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@heroui/react';
import { renderNotice } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NamesCheck } from '../../namesCheck';

type NamesCheckAlertProps = {
    check: Extract<
        NamesCheck,
        {
            status:
                'failed' | 'blocked' | 'namesNeedAttention' | 'axesToReview';
        }
    >;
};

/**
 * Why Check products stayed on Names. In danger tone: no answer (pressing
 * it again retries), the file's header problems or a refusal (one sentence
 * each, through the notices module), or names the decisions could not
 * resolve (their rows say why). In warning tone, since nothing is wrong:
 * categories the answer adds axes to that their rows had not shown (the
 * rows show them now; pressing it again goes on).
 */
export const NamesCheckAlert: React.FC<NamesCheckAlertProps> = ({ check }) => {
    const { t } = useTranslation('pages');

    return (
        <Alert
            status={check.status === 'axesToReview' ? 'warning' : 'danger'}
            {...getNodeTestIdProp('catalog-import-names-check-alert')}
        >
            <Alert.Indicator />
            <Alert.Content>
                {check.status === 'failed' ? (
                    <>
                        <Alert.Title>
                            {t('catalogImport.names.check.failed.title')}
                        </Alert.Title>
                        <Alert.Description>
                            {t('catalogImport.names.check.failed.description')}
                        </Alert.Description>
                    </>
                ) : null}
                {check.status === 'blocked' ? (
                    <>
                        <Alert.Title>
                            {t('catalogImport.names.check.blocked.title')}
                        </Alert.Title>
                        <Alert.Description>
                            <ul className="mt-1 flex flex-col gap-1">
                                {check.notices.map((notice) => (
                                    <li key={notice.key}>
                                        {renderNotice(t, notice)}
                                    </li>
                                ))}
                            </ul>
                        </Alert.Description>
                    </>
                ) : null}
                {check.status === 'namesNeedAttention' ? (
                    <>
                        <Alert.Title>
                            {t(
                                'catalogImport.names.check.namesNeedAttention.title',
                            )}
                        </Alert.Title>
                        <Alert.Description>
                            {t(
                                'catalogImport.names.check.namesNeedAttention.description',
                                { count: check.count },
                            )}
                        </Alert.Description>
                    </>
                ) : null}
                {check.status === 'axesToReview' ? (
                    <>
                        <Alert.Title>
                            {t('catalogImport.names.check.axesToReview.title')}
                        </Alert.Title>
                        <Alert.Description>
                            {t(
                                'catalogImport.names.check.axesToReview.description',
                                { count: check.count },
                            )}
                        </Alert.Description>
                    </>
                ) : null}
            </Alert.Content>
        </Alert>
    );
};
